import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Custom hook to determine round permissions for the current user
export const useRoundPermissions = (round, groups = []) => {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user || !round) {
      return {
        canEdit: false,
        canDelete: false,
        reason: 'No user or round data'
      };
    }

    // Handle solo rounds (no group_id)
    if (!round.group_id) {
      const isRoundCreator = round.created_by === user.id;
      return {
        canEdit: isRoundCreator,
        canDelete: isRoundCreator,
        reason: isRoundCreator ? 'Solo round creator' : 'Not round creator',
        isRoundCreator,
        isGroupOwner: false,
        isGroupAdmin: false,
        group: null
      };
    }

    // Find the group this round belongs to
    const group = groups.find(g => g.id === round.group_id);

    if (!group) {
      return {
        canEdit: false,
        canDelete: false,
        reason: 'Group not found'
      };
    }

    // Check if user is the round creator
    const isRoundCreator = round.created_by === user.id;
    
    // Check if user is group owner or admin
    const isGroupOwner = group.userRole === 'owner';
    const isGroupAdmin = group.userRole === 'admin';

    // Permission logic:
    // - Group owners can edit/delete any round in their group
    // - Group admins can edit/delete any round in their group  
    // - Round creators can edit/delete rounds they created
    const canEdit = isGroupOwner || isGroupAdmin || isRoundCreator;
    const canDelete = isGroupOwner || isGroupAdmin || isRoundCreator;

    // Determine the reason for permissions
    let reason = '';
    if (isGroupOwner) {
      reason = 'Group owner';
    } else if (isGroupAdmin) {
      reason = 'Group admin';  
    } else if (isRoundCreator) {
      reason = 'Round creator';
    } else {
      reason = 'No permissions';
    }

    return {
      canEdit,
      canDelete,
      reason,
      isRoundCreator,
      isGroupOwner,
      isGroupAdmin,
      group
    };
  }, [user, round, groups]);
};

export default useRoundPermissions;